import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useState, type ReactNode } from 'react';
import { fetchSelf, loginRequest, logoutRequest } from './api';
import type { BaseSelfUser, Credentials } from './types';

export interface AuthContextValue<TUser extends BaseSelfUser = BaseSelfUser> {
  user: TUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  /** Логин + обновление кэша. Навигацию после логина делает приложение. */
  login: (credentials: Credentials) => Promise<TUser | null>;
  /** Логаут + полная очистка кэша. Навигацию после логаута делает приложение. */
  logout: () => Promise<void>;
  isLoggingIn: boolean;
  isLoggingOut: boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export interface AuthProviderProps<TUser extends BaseSelfUser> {
  children: ReactNode;
  /** Загрузка/валидация текущего пользователя; по умолчанию BaseSelfUser через fetchSelf. */
  fetchUser?: () => Promise<TUser | null>;
}

export function AuthProvider<TUser extends BaseSelfUser = BaseSelfUser>({
  children,
  fetchUser = () => fetchSelf<TUser>(),
}: AuthProviderProps<TUser>) {
  const queryClient = useQueryClient();
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const { data: user, isLoading } = useQuery({
    queryKey: ['user', 'self'],
    queryFn: fetchUser,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      // 401/403 — нормальное состояние для неавторизованных, не ретраим
      if (error?.message?.includes('401') || error?.message?.includes('403')) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const login = async (credentials: Credentials): Promise<TUser | null> => {
    setIsLoggingIn(true);
    try {
      await loginRequest(credentials);
      // Отменяем активные запросы и кладём свежего пользователя в кэш
      await queryClient.cancelQueries();
      const freshUser = await fetchUser();
      queryClient.setQueryData(['user', 'self'], freshUser);
      return freshUser;
    } finally {
      setIsLoggingIn(false);
    }
  };

  const logout = async (): Promise<void> => {
    setIsLoggingOut(true);
    try {
      // Сначала локальная очистка — даже если сервер недоступен
      await queryClient.cancelQueries();
      queryClient.clear();
      await logoutRequest();
    } finally {
      setIsLoggingOut(false);
    }
  };

  const value: AuthContextValue = {
    user: user ?? null,
    isLoading,
    isAuthenticated: !!user,
    login,
    logout,
    isLoggingIn,
    isLoggingOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth<TUser extends BaseSelfUser = BaseSelfUser>(): AuthContextValue<TUser> {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context as AuthContextValue<TUser>;
}
